import { PopulationWidgetParams } from './population.model';

/**
 * Structured presets for the PopulationWidgetComponent.
 *
 * Each preset is a {@link PopulationWidgetParams} object.
 * These are the **single source of truth** — both Storybook stories
 * and the lesson JSON import/reference these presets.
 */

// ── Base ─────────────────────────────────────────────────

export const POPULATION_BASE: PopulationWidgetParams = {
  populationSize: 300,
};

// ── Lesson steps ─────────────────────────────────────────

export const POPULATION_INTRO: PopulationWidgetParams = {
  ...POPULATION_BASE,
  dotOpacity: 0.7,
};

export const SAMPLE_INTRO: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'random',
  samplePercentage: 10,
  sampleColor: '#3b82f6',
  sampleSeed: 149,
};

export const SAMPLE_BENEFIT: PopulationWidgetParams = {
  ...SAMPLE_INTRO,
};

export const SIMPLE_RANDOM: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'random',
  samplePercentage: 10,
  sampleColor: '#22c55e',
  sampleSeed: 149,
};

export const SYSTEMATIC_SAMPLING: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'systematic',
  sampleInterval: 10,
  sampleColor: '#3b82f6',
};

export const STRATIFIED_SAMPLING: PopulationWidgetParams = {
  ...POPULATION_BASE,
  demographicName: 'Gender',
  demographicCategories: [
    { label: 'Female', percentage: 60, color: '#ec4899' },
    { label: 'Male', percentage: 40, color: '#3b82f6' },
  ],
  sampleMethod: 'stratified',
  samplePercentage: 10,
  sampleColor: '#22c55e',
  sampleSeed: 149,
  showStratifiedDivider: true,
};

export const BIASED_SAMPLE: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'biased',
  samplePercentage: 10,
  sampleColor: '#ef4444',
  showBiasZone: true,
};

export const RELIABILITY: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'random',
  samplePercentage: 10,
  sampleColor: '#22c55e',
  sampleSeed: 149,
  reliabilityBars: [
    { label: 'Random Sample 1', width: 50, color: '#22c55e' },
    { label: 'Random Sample 2', width: 47, color: '#22c55e' },
    { label: 'Random Sample 3', width: 51.5, color: '#22c55e' },
    { label: 'Biased Sample', width: 72, color: '#ef4444' },
  ],
};

// ── Thumbnail presets (small, no label) ──────────────────

/** Small population grid for use as a thumbnail (e.g. table headers). */
export const POPULATION_THUMBNAIL: PopulationWidgetParams = {
  populationSize: 36,
  dotOpacity: 0.7,
  showPopulationCount: false,
};

/** Small sample thumbnail — shows a random subset highlighted. */
export const SAMPLE_THUMBNAIL: PopulationWidgetParams = {
  populationSize: 36,
  sampleMethod: 'random',
  samplePercentage: 25,
  sampleColor: '#3b82f6',
  sampleSeed: 149,
  showPopulationCount: false,
};

// ── Demographic presets (for Storybook) ──────────────────

export const GENDER_DEMOGRAPHICS: PopulationWidgetParams = {
  populationSize: 200,
  demographicName: 'Gender',
  demographicCategories: [
    { label: 'Male', percentage: 55, color: '#3b82f6' },
    { label: 'Female', percentage: 45, color: '#ec4899' },
  ],
};

export const BLOOD_TYPES: PopulationWidgetParams = {
  populationSize: 300,
  demographicName: 'Blood Type',
  demographicCategories: [
    { label: 'O', percentage: 44, color: '#ef4444' },
    { label: 'A', percentage: 42, color: '#3b82f6' },
    { label: 'B', percentage: 10, color: '#22c55e' },
    { label: 'AB', percentage: 4, color: '#a855f7' },
  ],
};

// ── Additional sampling presets ─────────────────────────

export const QUOTA_SAMPLING: PopulationWidgetParams = {
  ...POPULATION_BASE,
  demographicName: 'Gender',
  demographicCategories: [
    { label: 'Female', percentage: 60, color: '#ec4899' },
    { label: 'Male', percentage: 40, color: '#3b82f6' },
  ],
  sampleMethod: 'quota',
  samplePercentage: 10,
  sampleColor: '#a855f7',
  sampleSeed: 77,
  showStratifiedDivider: true,
};

export const CONVENIENCE_SAMPLING: PopulationWidgetParams = {
  ...POPULATION_BASE,
  sampleMethod: 'convenience',
  samplePercentage: 10,
  sampleColor: '#ef4444',
  showBiasZone: true,
};

/** Missing data intro — highlights which dots are missing with X markers. */
export const MISSING_DATA_HIGHLIGHT: PopulationWidgetParams = {
  ...POPULATION_BASE,
  missingPercentage: 15,
  missingDataMode: 'highlight',
  missingSeed: 123,
};

/** Listwise deletion — missing dots disappear completely. */
export const MISSING_DATA_LISTWISE: PopulationWidgetParams = {
  ...POPULATION_BASE,
  missingPercentage: 15,
  missingDataMode: 'removed',
  missingSeed: 123,
};

/** Pairwise deletion — missing dots stay but very faint. */
export const MISSING_DATA_PAIRWISE: PopulationWidgetParams = {
  ...POPULATION_BASE,
  missingPercentage: 15,
  missingDataMode: 'dimmed',
  missingSeed: 123,
};

/** Imputation — missing dots filled in with estimated values (blue). */
export const MISSING_DATA_IMPUTED: PopulationWidgetParams = {
  ...POPULATION_BASE,
  missingPercentage: 15,
  missingDataMode: 'imputed',
  imputedColor: '#3b82f6',
  missingSeed: 123,
};

// ── Preset registry (for custom component system) ───────
//
// Each key maps to the component inputs: { params: <PopulationWidgetParams> }.
// In a custom_component block, use a preset as base and spread overrides:
//
// ```json
// {
//   "component_id": "population",
//   "preset": "simple-random",
//   "inputs": { "params": { "samplePercentage": 20 } }
// }
// ```

export const POPULATION_PRESETS: Record<string, Record<string, unknown>> = {
  'population-intro': { params: POPULATION_INTRO },
  'population-thumbnail': { params: POPULATION_THUMBNAIL },
  'sample-intro': { params: SAMPLE_INTRO },
  'sample-thumbnail': { params: SAMPLE_THUMBNAIL },
  'sample-benefit': { params: SAMPLE_BENEFIT },
  'simple-random': { params: SIMPLE_RANDOM },
  systematic: { params: SYSTEMATIC_SAMPLING },
  stratified: { params: STRATIFIED_SAMPLING },
  biased: { params: BIASED_SAMPLE },
  quota: { params: QUOTA_SAMPLING },
  convenience: { params: CONVENIENCE_SAMPLING },
  reliability: { params: RELIABILITY },
  'missing-data-highlight': { params: MISSING_DATA_HIGHLIGHT },
  'missing-data-listwise': { params: MISSING_DATA_LISTWISE },
  'missing-data-pairwise': { params: MISSING_DATA_PAIRWISE },
  'missing-data-imputed': { params: MISSING_DATA_IMPUTED },
  'gender-demographics': { params: GENDER_DEMOGRAPHICS },
  'blood-types': { params: BLOOD_TYPES },
};
